
import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"
import { useIsMobile } from "@/hooks/use-mobile"

import { cn } from "@/lib/utils"

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & { 
    orientation?: "vertical" | "horizontal" | "both";
    preserveScrollOffset?: boolean; // New prop to preserve scroll position
    stickyHeader?: boolean; // New prop for sticky header functionality
  }
>(({ className, children, orientation = "vertical", preserveScrollOffset = false, stickyHeader = false, ...props }, ref) => {
  const isMobile = useIsMobile();
  // Reference to viewport for preserving scroll position
  const viewportRef = React.useRef<HTMLDivElement>(null);
  // Store scroll positions when content updates
  const [scrollPos, setScrollPos] = React.useState({ left: 0, top: 0 });

  // Effect to restore scroll position after DOM updates
  React.useEffect(() => {
    if (preserveScrollOffset && viewportRef.current) {
      const { left, top } = scrollPos;
      viewportRef.current.scrollLeft = left;
      viewportRef.current.scrollTop = top;
    }
  }, [preserveScrollOffset, scrollPos, children]);

  // Handler to save scroll position before content updates
  const handleScroll = React.useCallback(() => {
    if (preserveScrollOffset && viewportRef.current) {
      setScrollPos({
        left: viewportRef.current.scrollLeft,
        top: viewportRef.current.scrollTop
      });
    }
  }, [preserveScrollOffset]);

  // Use different classes for mobile devices
  const viewportClasses = cn(
    "h-full w-full rounded-[inherit]",
    isMobile ? "overflow-auto -webkit-overflow-scrolling-touch" : "",
    stickyHeader && "has-sticky-header"
  );

  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport 
        ref={viewportRef}
        onScroll={handleScroll}
        className={viewportClasses}
        style={isMobile ? { WebkitOverflowScrolling: 'touch' } : undefined}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      {!isMobile && (
        <>
          {orientation === "vertical" || orientation === "both" ? <ScrollBar orientation="vertical" /> : null}
          {orientation === "horizontal" || orientation === "both" ? <ScrollBar orientation="horizontal" /> : null}
          <ScrollAreaPrimitive.Corner />
        </>
      )}
    </ScrollAreaPrimitive.Root>
  )
})
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" &&
        "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb 
      className={cn(
        "relative flex-1 rounded-full",
        "dark:bg-secondary/80 bg-muted-foreground/40" // Theme-specific styling
      )} 
    />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName

export { ScrollArea, ScrollBar }
